import React, { useEffect, useRef, useState } from 'react'
import { ActionIcon, Box, Flex, Loader, TextInput } from '@mantine/core'
import { IconSearch, IconX } from '@tabler/icons-react'
import { capitalizeFirstLetter } from '@/lib/utilities'

interface AutocompleteTextInputProps {
    suggestions: string[]
    placeholder?: string
    handleClose: () => void
    searching: boolean
    searchTerm: string
    setSearchTerm: (searchTerm: string) => void
    onEnterPress?: () => void
    autoFocus?: boolean
}

export function AutocompleteTextInput({
    suggestions,
    placeholder,
    handleClose,
    searching,
    searchTerm,
    setSearchTerm,
    onEnterPress,
    autoFocus = false,
}: AutocompleteTextInputProps) {
    const [hint, setHint] = useState('')
    const touchStartX = useRef<number | null>(null)
    const SWIPE_THRESHOLD = 50 // minimum distance for a swipe

    const inputRef = useRef<HTMLInputElement>(null)
    // update hint on searchTerm change
    useEffect(() => {
        const query = searchTerm.toLowerCase().trim()
        const nextHint = query
            ? suggestions.find((s) => {
                  const lower = s.toLowerCase()
                  return lower.startsWith(query) && lower !== query
              }) || ''
            : ''
        setHint(nextHint)
    }, [searchTerm, suggestions])

    // Tab to accept the hint
    const handleKeyDown = (evt: React.KeyboardEvent) => {
        if (evt.key === 'Tab' && hint) {
            // Tab still accepts the hint
            evt.preventDefault()
            const capitalized = capitalizeFirstLetter(hint)
            setSearchTerm(capitalized)
            setHint('')
        } else if (evt.key === 'Enter' && searchTerm.trim() !== '' && onEnterPress) {
            // Enter always triggers search with current text, regardless of hint
            evt.preventDefault()
            onEnterPress()
        }
    }

    const handleTouchStart = (evt: React.TouchEvent) => {
        touchStartX.current = evt.touches[0].clientX
    }

    const handleTouchEnd = (evt: React.TouchEvent) => {
        if (!touchStartX.current || !hint) return

        const touchEndX = evt.changedTouches[0].clientX
        const swipeDistance = touchEndX - touchStartX.current

        if (swipeDistance > SWIPE_THRESHOLD) {
            // Right swipe detected
            const capitalized = capitalizeFirstLetter(hint)
            setSearchTerm(capitalized)
            setHint('')
        }

        touchStartX.current = null
    }

    return (
        <Box style={{ position: 'relative', width: '100%' }}>
            {renderNewTextInput()}
            {hint && inputRef.current && renderHint()}
        </Box>
    )

    function renderNewTextInput() {
        return (
            <TextInput
                ref={inputRef}
                value={searchTerm}
                placeholder={placeholder || ''}
                onKeyDown={handleKeyDown}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                size="sm"
                leftSection={renderSearchIcon()}
                rightSection={searching ? renderLoader() : renderXIcon()}
                onChange={(evt) => setSearchTerm(evt.currentTarget.value)}
                leftSectionWidth={48}
                rightSectionWidth={48}
                autoFocus={autoFocus}
                data-autofocus={autoFocus}
                data-testid="autocomplete-text-input"
                styles={{
                    root: { flex: 1 },
                    input: {
                        height: 48,
                        fontSize: 24,
                        backgroundColor: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                        zIndex: 2,
                    },
                }}
            />
        )
    }

    function renderSearchIcon() {
        return <IconSearch size={24} />
    }

    function renderLoader() {
        return <Loader size="xs" />
    }

    function renderXIcon() {
        return (
            <ActionIcon
                c="black"
                variant="outline"
                w={44}
                h={44}
                p={10}
                bd={'1px solid rgba(0, 0, 0, 0.20)'}
                radius={'999px'}
                data-testid="autocomplete-clear-button"
                onClick={() => {
                    if (searchTerm.trim() !== '') {
                        setSearchTerm('')
                    } else {
                        handleClose()
                    }
                }}
            >
                <IconX size={24} />
            </ActionIcon>
        )
    }

    function renderHint() {
        return (
            <Flex
                align={'flex-end'}
                pos={'absolute'}
                top={0}
                left={0}
                h={'100%'}
                lh={`${inputRef.current!.getBoundingClientRect().height}px`}
                c={'#aaa'}
                fz={getComputedStyle(inputRef.current!).fontSize}
                ff={getComputedStyle(inputRef.current!).fontFamily}
                pl={`${parseFloat(getComputedStyle(inputRef.current!).paddingLeft)}px`}
                style={{
                    pointerEvents: 'none',
                    zIndex: 1,
                }}
            >
                <Box component="span" style={{ visibility: 'hidden' }}>
                    {searchTerm}
                </Box>
                <Box component="span" style={{ whiteSpace: 'pre' }}>
                    {hint.slice(searchTerm.trim().length)}
                </Box>
            </Flex>
        )
    }
}
